import {
  CreateStoppageCommand,
  EquipmentDto,
  EquipmentPlannedDto,
  EquipmentType,
  StoppageReasonResponseDto,
  StoppageResponseDto,
  UpdateStoppageCommand,
} from '@operations/app/api'
import { EventTypes } from '@operations/messages/eventsTypes'
import equipmentService from '@operations/services/equipmentService'
import stoppageService from '@operations/services/stoppageService'
import { getCompleteDate } from '@operations/utils/format-date'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { BaseEntityStore } from './base/BaseEntityStore'

export interface StoppageFormData {
  id?: number
  stoppageReasonId?: number
  equipmentIds?: number[]
  carrierVisitId?: number
  notes?: string | null

  fromDate?: string
  fromTime?: string | null

  toDate?: string | null
  toTime?: string | null
}

export class StoppageStore extends BaseEntityStore<StoppageResponseDto> {
  selectedVisitId?: number
  visitEquipments: EquipmentPlannedDto[] = []
  fetchedAll = false

  constructor(private readonly messageBus: IMessageBus) {
    super()

    makeObservable(this, {
      visitEquipments: observable,

      setSelectedVisitId: action,

      cranes: computed,
      orderedItems: computed,
      openStoppages: computed,
    })

    this.messageBus.subscribeEvent<StoppageResponseDto>(
      EventTypes.StoppageUpserted,
      this.handleUpsertedEvent,
    )
    this.messageBus.subscribeEvent<number>(EventTypes.StoppageDeleted, this.handleDeletedEvent)

    this.messageBus.subscribeEvent<StoppageReasonResponseDto>(
      EventTypes.StoppageReasonUpserted,
      this.handleStoppageReasonUpsertedEvent,
    )

    this.messageBus.subscribeEvent<EquipmentDto>(
      EventTypes.EquipmentUpserted,
      this.handleEquipmentUpsertedEvent,
    )
  }

  public get cranes(): EquipmentPlannedDto[] {
    const cranes = this.visitEquipments.filter(x => x.equipmentType === EquipmentType.Sts)
    return _(cranes)
      .sortBy(x => x.name)
      .value()
  }

  public get orderedItems(): StoppageResponseDto[] {
    return _(this.items)
      .orderBy(x => x.from, 'desc')
      .value()
  }

  public get openStoppages(): number {
    return this.orderedItems.reduce((acc, stoppage) => acc + (!stoppage.to ? 1 : 0), 0)
  }

  public setSelectedVisitId(visitId: number) {
    this.selectedVisitId = visitId
  }

  public async fetchDataByVisitId(visitId: number) {
    if (visitId === this.selectedVisitId && this.fetchedAll) return

    this.fetchedAll = true

    const stoppages = await stoppageService.getByVesselVisitId(visitId)
    this.updateStoreItems(stoppages)
  }

  public async fetchOneById(id: number) {
    if (!id || this.items.some(x => x.id === id)) return

    const stoppage = await stoppageService.getById(id)
    this.updateStoreItem(stoppage, id)
  }

  public async loadEquipments(vesselVisitId: number) {
    const equipments = await equipmentService.loadPlanned(vesselVisitId)

    runInAction(() => {
      this.visitEquipments = equipments ?? []
    })
  }

  public onSubmit = async (data: StoppageFormData) => {
    const isEditMode = !!data.id

    const commonFields = {
      carrierVisitId: data.carrierVisitId!,
      stoppageReasonId: data.stoppageReasonId!,
      equipmentIds: data.equipmentIds ?? [],
      notes: data.notes,
      from: getCompleteDate(data.fromDate, data.fromTime),
      to: data.toDate ? getCompleteDate(data.toDate, data.toTime) : null,
    }

    if (isEditMode) {
      const cmd: UpdateStoppageCommand = {
        ...commonFields,
        id: data.id!,
      }

      await stoppageService.update(cmd)
    } else {
      const cmd: CreateStoppageCommand = {
        ...commonFields,
      }

      await stoppageService.create(cmd)
    }
  }

  public onDelete = async (id: number) => {
    await stoppageService.delete(id)
  }

  private readonly handleUpsertedEvent = (res: IEvent<StoppageResponseDto>) => {
    if (!res.payload) return

    if (res.payload.carrierVisitId !== this.selectedVisitId) return

    this.updateStoreItem(res.payload, res.payload.id)
  }

  private readonly handleDeletedEvent = (res: IEvent<number>) => {
    if (!res.payload) return

    this.deleteStoreItem(res.payload)
  }

  private readonly handleStoppageReasonUpsertedEvent = (res: IEvent<StoppageReasonResponseDto>) => {
    if (!res.payload) return

    let updated = false
    const updatedStoppages: StoppageResponseDto[] = this.items.map(stoppage => {
      if (stoppage.stoppageReason.id !== res.payload!.id) return stoppage

      updated = true
      return {
        ...stoppage,
        stoppageReason: {
          ...stoppage.stoppageReason,
          name: res.payload!.name,
          type: res.payload!.type,
          responsible: res.payload!.responsible,
        },
      }
    })

    if (updated) {
      this.updateStoreItems(updatedStoppages)
    }
  }

  private readonly handleEquipmentUpsertedEvent = (res: IEvent<EquipmentDto>) => {
    if (!res.payload) return

    let updated = false
    const updatedStoppages: StoppageResponseDto[] = this.items.map(stoppage => {
      const equipmentIndex = stoppage.equipments.findIndex(eq => eq.id === res.payload!.id)
      if (equipmentIndex < 0) return stoppage

      updated = true
      const updatedEquipment = {
        ...stoppage.equipments[equipmentIndex],
        name: res.payload!.name,
      }

      return {
        ...stoppage,
        equipments: stoppage.equipments.map((eq, index) =>
          index === equipmentIndex ? updatedEquipment : eq,
        ),
      }
    })

    if (updated) {
      this.updateStoreItems(updatedStoppages)
    }
  }
}
